import React, { useMemo, useState } from 'react';
import {
  ApplicationIcon,
  Box,
  ButtonIcon,
  ButtonSelect,
  Chip,
  Icon,
  Popover,
  Typography,
} from '@saviynt/design-system';
import cx from 'classnames';

import { CF_PRI_MED } from '../../../shared/constants';
import { useInvertedLocalize, useTypedSelector } from '../../../shared/hooks';
import type { RootState } from '../../../shared/types';
import { formatDisply, getBrowserTimezone } from '../../../shared/utils';
import AuditLogReportModal from '../AuditLogExportModal/AuditLogReportModal';
import type { RequestMetadata } from '../data-layer/auditLogTypes';
import { shouldShowEntitlementsRequestedRow } from '../data-layer/auditLogUtils';
import { useDateFormat } from '../hooks';
import {
  formatRange,
  formatRangeInTimeZone,
  getDisplayFormatForGlobalDateFormat,
  navigateBackToSource,
} from '../utils/utils';

import './AuditLogViewerHeader.css';

export interface AuditLogViewerHeaderProps {
  className?: string;
  onAuditorDetailsClick?: () => void;
}

/**
 * AuditLogViewerHeader Component
 * Header component for the audit log viewer page.
 * Provides navigation, actions, and title display.
 */
function AuditLogViewerHeader({
  className,
  onAuditorDetailsClick,
}: AuditLogViewerHeaderProps): React.ReactElement {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);

  const requestMetadata = useTypedSelector(
    (state: RootState): RequestMetadata | null =>
      state.aag.auditLog.requestMetadata
  );

  const isReadOnly = useTypedSelector(
    (state: RootState): boolean => state.aag.auditLog.isReadOnly
  );
  const l = useInvertedLocalize();
  const globalDateFormat = useDateFormat();
  const displayFormat = getDisplayFormatForGlobalDateFormat(globalDateFormat);

  // Safely derive time fields to avoid repeated optional chains in JSX
  const reqStart = requestMetadata?.requestStartedAt;
  const reqEnd = requestMetadata?.requestEndedAt;
  const reqTZ = requestMetadata?.sourceTimeZone;

  // Get browser's local timezone abbreviation for "Time Requested"
  const browserTZ = useMemo(() => {
    if (reqStart) {
      const date = new Date(reqStart);

      return getBrowserTimezone(date);
    }

    return '';
  }, [reqStart]);

  // Normalized entitlements list for popover rendering
  const requestedEntitlementDisplayNames: string[] =
    requestMetadata?.requestedEntitlementDisplayNames ?? [];

  const headerTitle = useMemo(
    () => formatDisply(requestMetadata?.requestHeaderDisplayName) ?? '-',
    [requestMetadata]
  );

  const isFfRoleDetail = Boolean(
    requestMetadata && requestMetadata.ffRoleName.trim() !== ''
  );

  const applicationIconLogo =
    requestMetadata?.endpointLogo !== undefined &&
    requestMetadata.endpointLogo !== ''
      ? requestMetadata.endpointLogo.replace('.svg', '')
      : undefined;

  return (
    <Box className={cx('Sod-AuditLogViewerHeader', className)} dataTheme='dark'>
      {/* Left Section - Navigation and Title */}
      <Box className='Sod-AuditLogViewerHeader-frame'>
        <Box className='Sod-AuditLogViewerHeader-frame-icon-text'>
          <Box className='Sod-AuditLogViewerHeader-frame-application-icon'>
            <ApplicationIcon logo={applicationIconLogo} size='large' />
          </Box>
          <Box className='Sod-AuditLogViewerHeader-frame-text'>
            <Box>
              <Typography lineLimit={3} kind='h1'>
                {headerTitle}
              </Typography>
            </Box>
            <Box className='Sod-AuditLogViewerHeader-frame-text-collapsed'>
              <Icon
                kind='AlertInfo'
                size='smallMedium'
                color='--color-foreground-primary-medium'
              />
              <Box>
                {isReadOnly ? (
                  <div
                    className='Sod-AuditLogViewerHeader-auditor-details'
                    onClick={onAuditorDetailsClick}
                    role='button'
                    tabIndex={0}
                    onKeyDown={(e: React.KeyboardEvent) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        e.stopPropagation();
                        onAuditorDetailsClick?.();
                      }
                    }}>
                    <Typography lineLimit={3} kind='body1' color={CF_PRI_MED}>
                      {l('Review and Request Details')}
                    </Typography>
                  </div>
                ) : (
                  <Popover
                    trigger={
                      <Typography
                        lineLimit={3}
                        kind='body1'
                        color={CF_PRI_MED}
                        dataTheme='dark'>
                        {l('Request Details')}
                      </Typography>
                    }
                    popoverContent={
                      <Box className='Sod-AuditLogViewerHeader-frame-popover-content'>
                        <Box className='Sod-AuditLogViewerHeader-frame-popover-line' />
                        <Box className='Sod-AuditLogViewerHeader-frame-popover-center'>
                          <Box className='Sod-AuditLogViewerHeader-frame-popover-center-content'>
                            <Box className='Sod-AuditLogViewerHeader-frame-popover-center-content-header'>
                              <Box className='Sod-AuditLogViewerHeader-frame-popover-center-content-header-text'>
                                <Typography lineLimit={3} kind='h3'>
                                  {l('Request Details')}
                                </Typography>
                              </Box>
                              <Box className='Sod-AuditLogViewerHeader-frame-popover-center-content-header-icon'>
                                <ButtonIcon
                                  size='medium'
                                  kind='ghost'
                                  ariaLabel={l('close')}
                                  icon={<Icon kind='Close' />}
                                  onClick={() => {
                                    setIsPopoverOpen(false);
                                  }}
                                />
                              </Box>
                            </Box>
                            <Box className='Sod-AuditLogViewerHeader-frame-popover-center-content-body'>
                              <Box className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem'>
                                <Box className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem-label'>
                                  <Typography lineLimit={3} kind='body3'>
                                    <span className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem-label-text'>
                                      {l('User')}
                                    </span>
                                  </Typography>
                                </Box>
                                <Box className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem-metaData'>
                                  <Typography lineLimit={3} kind='body2'>
                                    <span className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem-metaData-text'>
                                      {formatDisply(
                                        requestMetadata?.requestorUserDisplayName
                                      ) ?? '-'}
                                    </span>
                                  </Typography>
                                </Box>
                              </Box>
                              <Box className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem'>
                                <Box className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem-label'>
                                  <Typography lineLimit={3} kind='body3'>
                                    <span className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem-label-text'>
                                      {isFfRoleDetail
                                        ? l('Firefighter Role')
                                        : l('FFID')}
                                    </span>
                                  </Typography>
                                </Box>
                                <Box className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem-metaData'>
                                  <Typography lineLimit={3} kind='body2'>
                                    <span className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem-metaData-text'>
                                      {formatDisply(
                                        isFfRoleDetail
                                          ? requestMetadata?.ffRoleName
                                          : requestMetadata?.ffid
                                      ) ?? '-'}
                                    </span>
                                  </Typography>
                                </Box>
                              </Box>
                              <Box className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem'>
                                <Box className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem-label'>
                                  <Typography lineLimit={3} kind='body3'>
                                    <span className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem-label-text'>
                                      {l('Ticket Number')}
                                    </span>
                                  </Typography>
                                </Box>
                                <Box className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem-metaData'>
                                  <Typography lineLimit={3} kind='body2'>
                                    <span className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem-metaData-text'>
                                      {formatDisply(
                                        requestMetadata?.ticketNumber
                                      ) ?? '-'}
                                    </span>
                                  </Typography>
                                </Box>
                              </Box>
                              <Box className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem'>
                                <Box className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem-label'>
                                  <Typography lineLimit={3} kind='body3'>
                                    <span className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem-label-text'>
                                      {l('Justification (Requestor)')}
                                    </span>
                                  </Typography>
                                </Box>
                                <Box className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem-metaData'>
                                  <Typography lineLimit={3} kind='body2'>
                                    <span className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem-metaData-text'>
                                      {formatDisply(
                                        requestMetadata?.businessJustification
                                      ) ?? '-'}
                                    </span>
                                  </Typography>
                                </Box>
                              </Box>
                              <Box className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem'>
                                <Box className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem-label'>
                                  <Typography lineLimit={3} kind='body3'>
                                    <span className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem-label-text'>
                                      {l('Time Requested')}
                                    </span>
                                  </Typography>
                                </Box>
                                <Box className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem-metaData'>
                                  <Typography lineLimit={3} kind='body2'>
                                    <span className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem-metaData-text'>
                                      {reqStart && reqEnd && browserTZ
                                        ? formatRange(
                                            reqStart,
                                            reqEnd,
                                            browserTZ,
                                            displayFormat
                                          )
                                        : '-'}
                                    </span>
                                  </Typography>
                                </Box>
                              </Box>
                              <Box className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem'>
                                <Box className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem-label'>
                                  <Typography lineLimit={3} kind='body3'>
                                    <span className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem-label-text'>
                                      {l(
                                        'Time Requested in Source System Time'
                                      )}
                                    </span>
                                  </Typography>
                                </Box>
                                <Box className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem-metaData'>
                                  <Typography lineLimit={3} kind='body2'>
                                    <span className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem-metaData-text'>
                                      {reqStart && reqEnd && reqTZ
                                        ? formatRangeInTimeZone(
                                            reqStart,
                                            reqEnd,
                                            reqTZ,
                                            displayFormat
                                          )
                                        : '-'}
                                    </span>
                                  </Typography>
                                </Box>
                              </Box>
                              <Box className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem'>
                                <Box className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem-label'>
                                  <Typography lineLimit={3} kind='body3'>
                                    <span className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem-label-text'>
                                      {l('Request ID')}
                                    </span>
                                  </Typography>
                                </Box>
                                <Box className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem-metaData'>
                                  <Typography lineLimit={3} kind='body2'>
                                    <span className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem-metaData-text'>
                                      {formatDisply(
                                        requestMetadata?.requestId
                                      ) ?? '-'}
                                    </span>
                                  </Typography>
                                </Box>
                              </Box>
                              {shouldShowEntitlementsRequestedRow(
                                requestMetadata?.requestType
                              ) ? (
                                <Box className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem'>
                                  <Box className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem-label'>
                                    <Typography lineLimit={3} kind='body3'>
                                      <span className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem-label-text'>
                                        {l('Entitlements Requested')}
                                      </span>
                                    </Typography>
                                  </Box>
                                  <Box className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem-metaData'>
                                    {requestedEntitlementDisplayNames.length >
                                    0 ? (
                                      <Box className='Sod-AuditLogViewerHeader-chipList'>
                                        {requestedEntitlementDisplayNames.map(
                                          (name) => (
                                            <Chip
                                              key={String(name)}
                                              size='xSmall'
                                              label={String(name)}
                                            />
                                          )
                                        )}
                                      </Box>
                                    ) : (
                                      <Typography lineLimit={3} kind='body2'>
                                        <span className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem-metaData-text'>
                                          {l('-')}
                                        </span>
                                      </Typography>
                                    )}
                                  </Box>
                                </Box>
                              ) : null}
                              <Box className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem'>
                                <Box className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem-label'>
                                  <Typography lineLimit={3} kind='body3'>
                                    <span className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem-label-text'>
                                      {l('Approver')}
                                    </span>
                                  </Typography>
                                </Box>
                                <Box className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem-metaData'>
                                  <Typography lineLimit={3} kind='body2'>
                                    <span className='Sod-AuditLogViewerHeader-frame-popover-center-content-body-listItem-metaData-text'>
                                      {formatDisply(
                                        requestMetadata?.approverUserDisplayName
                                      ) ?? '-'}
                                    </span>
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>
                          </Box>
                        </Box>
                        <Box className='Sod-AuditLogViewerHeader-frame-popover-line' />
                      </Box>
                    }
                    isOpen={isPopoverOpen}
                    isCloseOnHover={false}
                    setIsOpen={setIsPopoverOpen}
                    isOpenOnHover
                    className='Example-Popover'
                    dataTheme='light'
                  />
                )}
              </Box>
            </Box>
          </Box>
        </Box>
        <Box className='Sod-AuditLogViewerHeader-frame-button'>
          <ButtonSelect
            kind='icon'
            size='medium'
            prefixIcon={
              <Icon
                kind='Export'
                size='smallMedium'
                color='--color-foreground-primary-medium'
              />
            }
            onClick={() => setIsReportModalOpen(true)}
          />
        </Box>
        <Box className='Sod-AuditLogViewerHeader-frame-icon-button'>
          <ButtonIcon
            size='medium'
            kind='ghost'
            ariaLabel={l('close')}
            icon={
              <Icon
                kind='Close'
                size='smallMedium'
                color='--color-foreground-primary-medium'
              />
            }
            onClick={navigateBackToSource}
          />
        </Box>
      </Box>
      {isReportModalOpen && requestMetadata ? (
        <AuditLogReportModal
          isOpen={isReportModalOpen}
          onClose={() => setIsReportModalOpen(false)}
          requestAccessKey={requestMetadata.requestAccessKey}
        />
      ) : null}
    </Box>
  );
}

AuditLogViewerHeader.defaultProps = {
  className: undefined,
  onAuditorDetailsClick: undefined,
};

export default AuditLogViewerHeader;
